import { supabase } from '../../lib/supabase'
import { toPence } from '../../utils/currency'
import { getCurrentUserId, verifyPropertyWriteAccess } from '../../lib/auth'
import type {
  RentPayment,
  RentPaymentInsert,
  RentPaymentUpdate,
  RentPaymentStatus,
} from '../../types/financial'

// ============================================================
// RENT PAYMENT MUTATIONS (create, update, mark paid, reset, delete)
// ============================================================

export async function createRentPayment(input: RentPaymentInsert): Promise<RentPayment> {
  if (!input.amount_due_pence || input.amount_due_pence <= 0) {
    throw new Error('amount_due_pence is required and must be positive')
  }
  if (!input.property_id) throw new Error('property_id is required')
  await verifyPropertyWriteAccess(input.property_id)

  // Always derive owner_id from properties.owner_id — never trust the caller's input.
  // A team member (or any caller) could supply a wrong owner_id; the row must be born correct.
  const { data: prop, error: propError } = await supabase
    .from('properties')
    .select('owner_id')
    .eq('id', input.property_id)
    .single()
  if (propError || !prop) throw new Error('Property not found')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { owner_id: _discarded, ...rest } = input
  const { data, error } = await supabase
    .from('rent_payments')
    .insert({ ...rest, owner_id: prop.owner_id })
    .select()
    .single()

  if (error) throw error

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_payment_created',
      entity_type: 'rent_payment',
      entity_id: data.id,
      new_data: {
        amount_due_pence: data.amount_due_pence,
        due_date: data.due_date,
        tenancy_id: data.tenancy_id,
        property_id: data.property_id,
        status: data.status,
      },
    })
  } catch { /* Non-blocking */ }

  return data
}

export async function updateRentPayment(id: string, input: RentPaymentUpdate): Promise<RentPayment> {
  // Always derive owner_id from properties.owner_id — never read the existing column value,
  // which may have been corrupted by a previous auth.uid() stamp. Self-heals on every write.
  const { data: existing } = await supabase
    .from('rent_payments')
    .select('property_id')
    .eq('id', id)
    .single()

  let landlordId: string | null = null
  if (existing?.property_id) {
    const { data: prop } = await supabase
      .from('properties')
      .select('owner_id')
      .eq('id', existing.property_id)
      .single()
    landlordId = prop?.owner_id ?? null
  }

  const { data, error } = await supabase
    .from('rent_payments')
    .update({ ...input, owner_id: landlordId })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  return data
}

export async function markRentAsPaid(
  id: string,
  paidAmount: number,
  paidDate: string,
  paymentMethod?: string,
  paymentReference?: string
): Promise<RentPayment> {
  // Fetch payment with tenancy and property details for auto-linking.
  // RLS "Property owners can manage rent payments" verifies ownership via tenancy chain.
  const { data: payment } = await supabase
    .from('rent_payments')
    .select(`
      id,
      amount,
      amount_due_pence,
      amount_paid_pence,
      paid_amount,
      status,
      due_date,
      tenancy:tenancies!inner(
        id,
        property_id,
        property:properties!inner(id, address_line1, owner_id)
      )
    `)
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  // Cumulative: add new payment to any existing partial amount
  const newPaidPence = toPence(paidAmount)
  const existingPaidPence = payment?.amount_paid_pence ?? 0
  const existingPaidPounds = payment?.paid_amount ?? 0
  const cumulativePaidPence = existingPaidPence + newPaidPence
  const cumulativePaidPounds = existingPaidPounds + paidAmount
  const status = cumulativePaidPence >= (payment?.amount_due_pence ?? 0) ? 'paid' : 'partial'

  const updated = await updateRentPayment(id, {
    status,
    paid_amount: cumulativePaidPounds,
    amount_paid_pence: cumulativePaidPence,
    paid_date: paidDate,
    payment_method: paymentMethod || null,
    payment_reference: paymentReference || null
  })

  // Auto-link: Create financial transaction for this rent payment
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tenancyData = payment?.tenancy as any
  if (payment && tenancyData) {
    const tenancy = {
      id: tenancyData.id as string,
      property_id: tenancyData.property_id as string,
      property: {
        id: tenancyData.property?.id as string,
        address_line1: tenancyData.property?.address_line1 as string,
        owner_id: tenancyData.property?.owner_id as string,
      }
    }

    try {
      const dueMonth = new Date(payment.due_date).toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric'
      })

      // Count existing transactions to label partials (e.g. "partial 2")
      const { count: txCount } = await supabase
        .from('financial_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('rent_payment_id', id)
        .is('deleted_at', null)

      const partialNum = (txCount ?? 0) + 1
      const isPartial = status === 'partial' || partialNum > 1
      const description = isPartial
        ? `Rent payment (partial ${partialNum}) - ${tenancy.property.address_line1} (${dueMonth})`
        : `Rent payment - ${tenancy.property.address_line1} (${dueMonth})`

      await supabase.from('financial_transactions').insert({
        owner_id: tenancy.property.owner_id,
        property_id: tenancy.property_id,
        tenancy_id: tenancy.id,
        rent_payment_id: id,
        type: 'income',
        category: 'rent',
        description,
        amount_net_pence: newPaidPence,
        amount_vat_pence: 0,
        amount_total_pence: newPaidPence,
        vat_rate: 0,
        transaction_date: paidDate,
        is_tax_deductible: false,
        source: 'rent_auto',
        source_ref: `rent_payment_${id}`,
        xero_sync_status: 'excluded',
      })
    } catch (err) {
      // Non-blocking: log but don't fail the rent payment update
      console.error('Failed to auto-link rent payment to financial transaction:', err)
    }
  }

  // Xero rent invoices are created proactively on tenancy activation and via daily cron.
  // Mark-as-paid does NOT trigger any Xero action — intentional.
  // The DRAFT invoice already exists in Xero for the accountant to approve and reconcile.

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_payment_recorded',
      entity_type: 'rent_payment',
      entity_id: id,
      new_data: { amount: paidAmount, cumulative_pence: cumulativePaidPence, paid_date: paidDate, payment_method: paymentMethod, status },
    })
  } catch { /* Non-blocking */ }

  return updated
}

export async function generateRentSchedule(tenancyId: string, monthsAhead: number = 2): Promise<void> {
  // Verify ownership through the tenancy's property
  const { data: tenancy, error: tenancyError } = await supabase
    .from('tenancies')
    .select('property_id')
    .eq('id', tenancyId)
    .single()

  if (tenancyError || !tenancy) throw new Error('Tenancy not found')
  await verifyPropertyWriteAccess(tenancy.property_id)

  const { error } = await supabase.rpc('generate_rent_schedule', {
    p_tenancy_id: tenancyId,
    p_months_ahead: monthsAhead
  })

  if (error) throw error

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_schedule_generated',
      entity_type: 'rent_schedule',
      entity_id: tenancyId,
      new_data: {
        tenancy_id: tenancyId,
        months_ahead: monthsAhead,
        property_id: tenancy.property_id,
      },
    })
  } catch { /* Non-blocking */ }

  // Create DRAFT Xero invoices for the first 3 rent payments — fire-and-forget.
  // Only if the landlord has an active Xero connection.
  try {
    const { data: conn } = await supabase
      .from('xero_connections')
      .select('is_active')
      .eq('owner_id', (await getCurrentUserId()))
      .eq('is_active', true)
      .maybeSingle()

    if (conn) {
      supabase.functions.invoke('xero-sync-rent', {
        body: { tenancy_id: tenancyId, limit: 3 },
      }).catch(() => {})
    }
  } catch { /* Non-fatal */ }
}

// ============================================================
// RENT PAYMENT MUTATIONS (reset, delete, update amount)
// ============================================================

/**
 * Reset a rent payment to unpaid status.
 * Recalculates status based on due date.
 */
export async function resetRentPayment(id: string): Promise<RentPayment> {
  const today = new Date().toISOString().split('T')[0]

  // Get current payment to determine new status + property_id for cache refresh.
  // RLS "Property owners can manage rent payments" verifies ownership via tenancy chain.
  const { data: payment } = await supabase
    .from('rent_payments')
    .select('due_date, property_id, owner_id, xero_invoice_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!payment) throw new Error('Rent payment not found')

  // Always derive owner_id from properties.owner_id — never read the existing column value.
  // Self-heals any previously-corrupted rows on every write.
  let landlordId: string | null = null
  if (payment.property_id) {
    const { data: prop } = await supabase
      .from('properties')
      .select('owner_id')
      .eq('id', payment.property_id)
      .single()
    landlordId = prop?.owner_id ?? null
  }

  // Soft-delete orphaned financial transaction linked to this rent payment
  try {
    await supabase.rpc('soft_delete_record', {
      target_table: 'financial_transactions',
      record_id: id,
      filter_column: 'rent_payment_id',
    })
  } catch { /* Non-blocking */ }

  // Rent payment reset does NOT void or delete the Xero invoice — intentional.
  // The DRAFT invoice is left in Xero for the accountant to handle.

  // Calculate status based on due date
  let newStatus: RentPaymentStatus = 'upcoming'
  const dueDate = new Date(payment.due_date)
  const todayDate = new Date(today)

  if (dueDate < todayDate) {
    newStatus = 'overdue'
  } else if (dueDate.toISOString().split('T')[0] === today) {
    newStatus = 'due'
  }

  const { data, error } = await supabase
    .from('rent_payments')
    .update({
      status: newStatus,
      paid_amount: null,
      amount_paid_pence: 0,
      xero_last_synced_pence: 0,
      paid_date: null,
      payment_method: null,
      payment_reference: null,
      owner_id: landlordId,
    })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_payment_reset',
      entity_type: 'rent_payment',
      entity_id: id,
      new_data: { new_status: newStatus },
    })
  } catch { /* Non-blocking */ }

  return data
}

/**
 * Delete a rent payment and its linked financial transaction.
 */
export async function deleteRentPayment(id: string): Promise<void> {
  // Fetch rent payment data for cache refresh.
  // RLS "Property owners can manage rent payments" verifies ownership via tenancy chain.
  const { data: rentPayment } = await supabase
    .from('rent_payments')
    .select('property_id, owner_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!rentPayment) throw new Error('Rent payment not found')

  // First soft-delete any linked financial transaction
  const { data: transaction } = await supabase
    .from('financial_transactions')
    .select('id')
    .eq('rent_payment_id', id)
    .is('deleted_at', null)
    .maybeSingle()

  if (transaction) {
    await supabase.rpc('soft_delete_record', {
      target_table: 'financial_transactions',
      record_id: transaction.id,
    })
  }

  // Soft-delete the rent payment
  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'rent_payments',
    record_id: id,
  })

  if (error) throw error

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_payment_deleted',
      entity_type: 'rent_payment',
      entity_id: id,
      old_data: { linked_transaction_id: transaction?.id },
    })
  } catch { /* Non-blocking */ }

}

/**
 * Update a rent payment amount and sync to linked transaction.
 */
export async function updateRentPaymentAmount(id: string, newAmount: number): Promise<RentPayment> {
  // Always derive owner_id from properties.owner_id — never read the existing column value.
  // Self-heals any previously-corrupted rows on every write.
  const { data: existing } = await supabase
    .from('rent_payments')
    .select('property_id')
    .eq('id', id)
    .single()

  let landlordId: string | null = null
  if (existing?.property_id) {
    const { data: prop } = await supabase
      .from('properties')
      .select('owner_id')
      .eq('id', existing.property_id)
      .single()
    landlordId = prop?.owner_id ?? null
  }

  const { data, error } = await supabase
    .from('rent_payments')
    .update({ amount: newAmount, owner_id: landlordId })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  // If paid, also update the linked financial transaction
  if (data.status === 'paid' && data.amount_paid_pence) {
    const { data: transaction } = await supabase
      .from('financial_transactions')
      .select('id')
      .eq('rent_payment_id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (transaction) {
      const amountPence = data.amount_paid_pence
      await supabase
        .from('financial_transactions')
        .update({
          amount_net_pence: amountPence,
          amount_total_pence: amountPence
        })
        .eq('id', transaction.id)
    }
  }

  // Audit trail
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'rent_payment_amount_updated',
      entity_type: 'rent_payment',
      entity_id: id,
      new_data: { new_amount: newAmount },
    })
  } catch { /* Non-blocking */ }

  return data
}
